<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content modal-content-large">
                <div class="popup-dialog">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ formTitle }}</h1>
                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <g-form :action="formEndpoint" :form="form" :method="formMethod"
                            @success="formSuccess" :message="$t('notice.saved')">
                        <div class="popup-body popup-rows">
                            <!-- General Settings Section -->
                            <div class="form-section">
                                <h3 class="form-section-title">
                                    <i class="icon">settings</i>
                                    {{ $t('admin.support_prompts.general_settings') }}
                                </h3>
                                <div class="form-section-content">
                                    <div class="columns">
                                        <div class="column is-one-fifth">
                                            <label class="field-label">{{ $t('admin.support_prompts.subject') }}</label>
                                        </div>
                                        <div class="column">
                                            <g-select
                                                name="subject"
                                                :label="$t('admin.support_prompts.subject')"
                                                :options="subjectOptions"
                                                :disabled="!isCreateNewForm"
                                            />
                                        </div>
                                    </div>

                                    <div class="columns">
                                        <div class="column is-one-fifth">
                                            <label class="field-label">{{ $t('admin.support_prompts.company') }}</label>
                                        </div>
                                        <div class="column">
                                            <g-input
                                                name="company_id"
                                                type="number"
                                                :label="$t('admin.support_prompts.company_id_hint')"
                                            />
                                            <p class="help">{{ $t('admin.support_prompts.company_hint') }}</p>
                                        </div>
                                    </div>

                                    <div class="columns">
                                        <div class="column is-one-fifth">
                                            <label class="field-label">{{ $t('admin.support_prompts.status') }}</label>
                                        </div>
                                        <div class="column">
                                            <g-switch
                                                name="is_active"
                                                :label="$t('admin.support_prompts.is_active')"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- AI Configuration Section -->
                            <div class="form-section">
                                <h3 class="form-section-title">
                                    <i class="icon">smart_toy</i>
                                    {{ $t('admin.support_prompts.ai_settings') }}
                                </h3>
                                <div class="form-section-content">
                                    <div class="columns">
                                        <div class="column is-half">
                                            <g-select
                                                name="ai_service"
                                                :label="$t('admin.support_prompts.ai_service')"
                                                :options="aiServiceOptions"
                                                @input="onServiceChange"
                                            />
                                            <p class="help">{{ $t('admin.support_prompts.ai_service_hint') }}</p>
                                        </div>
                                        <div class="column is-half">
                                            <g-select
                                                name="ai_model"
                                                :label="$t('admin.support_prompts.ai_model')"
                                                :options="aiModelOptions"
                                                :disabled="!form.ai_service"
                                            />
                                            <p class="help">{{ $t('admin.support_prompts.ai_model_hint') }}</p>
                                        </div>
                                    </div>
                                    <div class="columns">
                                        <div class="column is-half">
                                            <g-switch
                                                name="enable_thinking"
                                                :label="$t('admin.support_prompts.enable_thinking')"
                                            />
                                            <p class="help">{{ $t('admin.support_prompts.enable_thinking_hint') }}</p>
                                        </div>
                                        <div class="column is-half">
                                            <g-input
                                                name="temperature"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="2"
                                                :label="$t('admin.support_prompts.temperature')"
                                                :disabled="!!form.enable_thinking"
                                            />
                                            <p class="help">{{ form.enable_thinking ? $t('admin.support_prompts.temperature_thinking_hint') : $t('admin.support_prompts.temperature_hint') }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Prompts Section -->
                            <div class="form-section">
                                <h3 class="form-section-title">
                                    <i class="icon">description</i>
                                    {{ $t('admin.support_prompts.prompts') }}
                                </h3>
                                <div class="form-section-content">
                                    <div class="prompt-field">
                                        <div class="prompt-header">
                                            <label class="field-label">{{ $t('admin.support_prompts.system_prompt') }}</label>
                                            <button type="button" class="button is-small is-light" @click="loadDefaultSystemPrompt">
                                                <i class="icon is-small">restart_alt</i>
                                                {{ $t('admin.support_prompts.load_default') }}
                                            </button>
                                        </div>
                                        <p class="help mb-2">{{ $t('admin.support_prompts.system_prompt_hint') }}</p>
                                        <g-input
                                            name="system_prompt"
                                            type="textarea"
                                            rows="8"
                                            :autosize="false"
                                        />
                                    </div>

                                    <div class="prompt-field">
                                        <div class="prompt-header">
                                            <label class="field-label">{{ $t('admin.support_prompts.task_prompt') }}</label>
                                            <button type="button" class="button is-small is-light" @click="loadDefaultTaskPrompt">
                                                <i class="icon is-small">restart_alt</i>
                                                {{ $t('admin.support_prompts.load_default') }}
                                            </button>
                                        </div>
                                        <p class="help mb-2">{{ $t('admin.support_prompts.task_prompt_hint') }}</p>
                                        <g-input
                                            name="task_prompt"
                                            type="textarea"
                                            rows="12"
                                            :autosize="false"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="popup-footer">
                            <a @click.prevent="destroy" href="#" role="button">{{ $t('submit_dialog.cancel') }}</a>
                            <g-button>{{ $t('submit_dialog.submit') }}</g-button>
                        </div>
                    </g-form>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    props: {
        id: [String, Number]
    },

    data() {
        const object = this.id ? this.$store.getters['adminSupportPromptTemplates/getById'](this.id) : null
        const formData = object ? {
            subject: object.subject,
            company_id: object.company_id,
            system_prompt: object.system_prompt,
            task_prompt: object.task_prompt,
            ai_service: object.ai_service,
            ai_model: object.ai_model,
            enable_thinking: object.enable_thinking ?? true,
            temperature: object.temperature,
            is_active: object.is_active,
        } : {
            subject: 'delivery_inquiry',
            company_id: null,
            system_prompt: null,
            task_prompt: null,
            ai_service: null,
            ai_model: null,
            enable_thinking: true,
            temperature: null,
            is_active: true,
        }

        return {
            object: object,
            form: new Form(formData),
            defaults: null,
            aiServices: {},
            aiModels: {},
            subjectOptions: [
                {value: 'delivery_inquiry', name: 'Delivery Inquiry'},
            ],
        }
    },

    computed: {
        isCreateNewForm() {
            return this.object == null
        },

        formTitle() {
            return this.isCreateNewForm
                ? this.$t('admin.support_prompts.create.title')
                : this.$t('admin.support_prompts.edit.title')
        },

        formEndpoint() {
            let endpoint = '/api/admin/support-prompt-template'
            if (this.isCreateNewForm === false) {
                endpoint += `/${this.id}`
            }
            return endpoint
        },

        formMethod() {
            return this.isCreateNewForm ? 'post' : 'patch'
        },

        aiServiceOptions() {
            const options = [{value: null, name: this.$t('admin.support_prompts.ai_service_default')}]
            Object.entries(this.aiServices).forEach(([key, label]) => {
                options.push({value: key, name: label})
            })
            return options
        },

        aiModelOptions() {
            const options = [{value: null, name: this.$t('admin.support_prompts.ai_model_default')}]
            if (this.form.ai_service && this.aiModels[this.form.ai_service]) {
                Object.entries(this.aiModels[this.form.ai_service]).forEach(([key, label]) => {
                    options.push({value: key, name: label})
                })
            }
            return options
        }
    },

    created() {
        this.loadDefaults()
    },

    methods: {
        formSuccess(response) {
            const action = this.isCreateNewForm
                ? 'adminSupportPromptTemplates/add'
                : 'adminSupportPromptTemplates/update'

            this.$store
                .dispatch(action, {object: response.data})
                .then(this.destroy)
        },

        loadDefaults() {
            axios.get('/api/admin/support-prompt-template/defaults')
                .then(response => {
                    this.defaults = response.data.defaults
                    this.aiServices = response.data.ai_services || {}
                    this.aiModels = response.data.ai_models || {}
                    this.subjectOptions = response.data.subjects.map(s => ({
                        value: s,
                        name: s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                    }))
                })
                .catch(error => {
                    console.error('Failed to load defaults:', error)
                })
        },

        onServiceChange() {
            // Reset model when service changes
            this.form.ai_model = null
        },

        loadDefaultSystemPrompt() {
            if (this.defaults && this.form.subject) {
                this.form.system_prompt = this.defaults[this.form.subject]?.system_prompt || ''
            }
        },

        loadDefaultTaskPrompt() {
            if (this.defaults && this.form.subject) {
                this.form.task_prompt = this.defaults[this.form.subject]?.task_prompt || ''
            }
        },
    },
}
</script>

<style scoped lang="scss">
.modal-content-large {
    max-width: 900px;
    width: 90%;
}

.form-section {
    background: #fafafa;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;

    &:last-child {
        margin-bottom: 0;
    }
}

.form-section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: #363636;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e8e8e8;

    .icon {
        color: #7a7a7a;
        font-size: 1.25rem;
    }
}

.form-section-content {
    .columns {
        margin-bottom: 0.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.field-label {
    font-weight: 500;
    color: #4a4a4a;
    display: block;
    margin-bottom: 0.25rem;
}

.prompt-field {
    margin-bottom: 1.5rem;

    &:last-child {
        margin-bottom: 0;
    }
}

.prompt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;

    .field-label {
        margin-bottom: 0;
    }

    .button {
        gap: 0.25rem;
    }
}
</style>
